import { useState } from 'owa-react';
import React from 'react';
import loc, { format } from 'owa-localize';
import { observer } from 'owa-mobx-react';
import {
    makeStyles,
    mergeClasses,
    Button,
    Input,
    MenuList,
    MenuItemRadio,
} from '@fluentui/react-components';
import { iconRegularClassName } from '@fluentui/react-icons';
import type {
    InputOnChangeData,
    MenuCheckedValueChangeEvent,
    MenuCheckedValueChangeData,
} from '@fluentui/react-components';
import CategoryIcon from 'owa-categories/lib/components/CategoryIcon';
import getMasterCategoryList from 'owa-categories/lib/utils/getMasterCategoryList';
import type { MailboxInfo } from 'owa-client-types';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import { type SortColumn } from 'owa-mail-list-store';

import { getJumpToCategoryOptions } from '../helpers/getJumpToCategoryOptions';
import { getJumpToSizeOptions } from '../helpers/getJumpToSizeOptions';
import { getJumpToImportanceOptions } from '../helpers/getJumpToImportanceOptions';
import executeJumpTo from '../orchestrators/executeJumpTo';
import {
    goButtonLabel,
    goButtonLabelDateTimeSort,
    jumpToSenderInputPlaceholder,
    jumpToSubjectInputPlaceholder,
    jumpToRecipientInputPlaceholder,
} from './MailListJumpToPopover.locstring.json';
import {
    popoverSurfaceContainer,
    goButton,
    jumpToInput,
    checkmarkDisplay,
    categoryMenuScrollContainer,
    categoryMenuContainer,
    categoryIcon,
    popoverSurfaceText,
    full,
} from './MailListJumpToPopover.scss';

import { LazyJumpToDateCalloutContent } from '../lazyComponents';

import classnames from 'owa-classnames';

export interface JumpToCalloutContentProps {
    tableView: TableView;
    mailboxInfo: MailboxInfo;
    onClose: () => void;
    onKeyDownCallback: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
}

const checkmarkProps = { className: checkmarkDisplay };

const useStyles = makeStyles({
    menuItem: {
        maxWidth: 'unset',
        minWidth: 'max-content',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': {
            [`& .${iconRegularClassName}`]: {
                display: 'inline-flex !important',
            },
        },
    },
});

const defaultExport: React.FunctionComponent<JumpToCalloutContentProps> = observer(
    function JumpToCalloutContent(props: JumpToCalloutContentProps) {
        const { mailboxInfo, tableView, onClose, onKeyDownCallback } = props;
        const [inputValue, setInputValue] = useState('');
        const [selectedDate, setSelectedDate] = useState<string | null>(null);
        const [isDateComponentReady, setIsDateComponentReady] = useState(false);
        const goButtonRef = React.useRef<HTMLButtonElement>(null);
        const sortColumn = (tableView?.tableQuery as MailFolderTableQuery)?.sortBy?.sortColumn;
        const sortDirection = (tableView?.tableQuery as MailFolderTableQuery)?.sortBy
            ?.sortDirection;
        const categoryList = getMasterCategoryList(mailboxInfo);
        const styles = useStyles();
        const densityMode = getDensityModeCssClass(full, undefined, undefined);
        const isDateTimeSort = sortColumn === 1;

        // Show the Go button when the user needs to input some data and then trigger the jump.
        // For the sort columns where we have a dropdown, we don't need the Go button because we trigger the jump on selection.
        const shouldShowGoButton = React.useMemo(() => {
            return sortColumn === 1 || sortColumn === 8 || sortColumn === 3 || sortColumn === 9;
        }, [sortColumn]);

        // Focus the Go button when the callout opens only in DateTime sort, and only after the date component is ready
        React.useEffect(() => {
            if (
                isDateTimeSort &&
                shouldShowGoButton &&
                isDateComponentReady &&
                goButtonRef.current
            ) {
                goButtonRef.current?.focus();
            }
        }, [isDateTimeSort, shouldShowGoButton, isDateComponentReady]);

        const onGoClicked = React.useCallback(() => {
            onClose();
            if (inputValue !== '') {
                executeJumpTo(tableView, inputValue /*restrictionValue*/);
            }
        }, [inputValue, onClose, tableView]);

        const setInputValueCallback = React.useCallback((input: string) => {
            setInputValue(input);
        }, []);

        const onSelectedDateChange = React.useCallback((date: string | null) => {
            setSelectedDate(date);
        }, []);

        const onDateComponentReady = React.useCallback(() => {
            setIsDateComponentReady(true);
        }, []);

        const handleInputChange = React.useCallback(
            (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                ev.stopPropagation();
                if (data.value !== undefined) {
                    setInputValue(data.value);
                } else {
                    setInputValue('');
                }
            },
            []
        );

        const onCheckedValueChange = React.useCallback(
            (ev: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
                ev.stopPropagation();
                onClose();
                const checkedItem = data.checkedItems?.[0];
                if (checkedItem !== undefined) {
                    executeJumpTo(tableView, checkedItem /*restrictionValue*/);
                }
            },
            [onClose, tableView]
        );

        const onKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                    onGoClicked();
                    event.preventDefault();
                    event.stopPropagation();
                } else if (event.key === 'Escape') {
                    onKeyDownCallback(event);
                }
            },
            [onGoClicked, onKeyDownCallback]
        );

        const getCategoryIcon = React.useCallback(
            (categoryName: string) => {
                return (
                    <CategoryIcon
                        categoryName={categoryName}
                        iconClassName={categoryIcon}
                        categoryList={categoryList}
                        mailboxInfo={mailboxInfo}
                    />
                );
            },
            [categoryList, mailboxInfo]
        );

        const inputForSortColumn = React.useMemo((): React.JSX.Element | null => {
            switch (sortColumn) {
                case 3:
                case 8:
                case 9:
                    const placeholderText =
                        sortColumn === 3
                            ? loc(jumpToSenderInputPlaceholder)
                            : sortColumn === 8
                            ? loc(jumpToSubjectInputPlaceholder)
                            : sortColumn === 9
                            ? loc(jumpToRecipientInputPlaceholder)
                            : '';
                    return (
                        <Input
                            className={classnames(jumpToInput, popoverSurfaceText, densityMode)}
                            placeholder={placeholderText}
                            onChange={handleInputChange}
                            onKeyDown={onKeyDown}
                        />
                    );
                case 5:
                    return (
                        <MenuList onCheckedValueChange={onCheckedValueChange}>
                            {getJumpToImportanceOptions().map(option => {
                                return (
                                    <MenuItemRadio
                                        key={option.value}
                                        className={mergeClasses(
                                            styles.menuItem,
                                            popoverSurfaceText,
                                            densityMode
                                        )}
                                        checkmark={checkmarkProps}
                                        name="importance"
                                        value={option.value}
                                    >
                                        {loc(option.text)}
                                    </MenuItemRadio>
                                );
                            })}
                        </MenuList>
                    );
                case 1:
                    return (
                        <LazyJumpToDateCalloutContent
                            tableView={tableView}
                            onCheckedValueChange={onCheckedValueChange}
                            setInputValue={setInputValueCallback}
                            onSelectedDateChange={onSelectedDateChange}
                            onComponentReady={onDateComponentReady}
                        />
                    );
                case 14:
                    return (
                        <div className={classnames(categoryMenuScrollContainer, 'customScrollBar')}>
                            <MenuList
                                className={categoryMenuContainer}
                                onCheckedValueChange={onCheckedValueChange}
                            >
                                {getJumpToCategoryOptions(categoryList).map(option => {
                                    return (
                                        <MenuItemRadio
                                            key={option.value}
                                            checkmark={checkmarkProps}
                                            name="category"
                                            value={option.value}
                                            className={mergeClasses(
                                                styles.menuItem,
                                                popoverSurfaceText,
                                                densityMode
                                            )}
                                        >
                                            <>
                                                {getCategoryIcon(option.value)}
                                                {option.text}
                                            </>
                                        </MenuItemRadio>
                                    );
                                })}
                            </MenuList>
                        </div>
                    );
                case 7:
                    return (
                        <MenuList onCheckedValueChange={onCheckedValueChange}>
                            {getJumpToSizeOptions(sortDirection).map(option => {
                                return (
                                    <MenuItemRadio
                                        key={option.value}
                                        className={mergeClasses(
                                            styles.menuItem,
                                            popoverSurfaceText,
                                            densityMode
                                        )}
                                        checkmark={checkmarkProps}
                                        name="size"
                                        value={option.value.toString()}
                                    >
                                        {loc(option.text)}
                                    </MenuItemRadio>
                                );
                            })}
                        </MenuList>
                    );
                default:
                    return null;
            }
        }, [
            sortColumn,
            sortDirection,
            tableView,
            onCheckedValueChange,
            onKeyDown,
            onSelectedDateChange,
            onDateComponentReady,
        ]);

        return (
            <div className={popoverSurfaceContainer}>
                {inputForSortColumn}
                {shouldShowGoButton && (
                    <Button
                        ref={goButtonRef}
                        onClick={onGoClicked}
                        aria-label={
                            isDateTimeSort && selectedDate
                                ? format(loc(goButtonLabelDateTimeSort), selectedDate)
                                : loc(goButtonLabel)
                        }
                        className={classnames(goButton, popoverSurfaceText, densityMode)}
                    >
                        {loc(goButtonLabel)}
                    </Button>
                )}
            </div>
        );
    },
    'JumpToCalloutContent'
);
export default defaultExport;
